import type { QueryClient, QueryKey } from '@tanstack/react-query'
import { tableEditingStore } from '@/store/table-editing.store'
import { hashKey } from '@tanstack/react-query'

import type { RecordQueryScope } from './record-query-scope'

type TaskReaderScope = RecordQueryScope & {
  ownerUserId?: string | null
  assertUnchanged?: () => void
  isEditing?: () => boolean
  subscribeEditing?: (listener: () => void) => () => void
}

const TASK_READER_ROOTS = new Set([
  'tasks',
  'task',
  'my-tasks',
  'subtasks',
  'project-subtask-index',
  'projects',
  'project',
])

export function isTaskCollectionNotice(data: unknown): data is {
  organizationId?: string
  _eventId?: string
  _readerDenied?: boolean
} {
  if (!data || typeof data !== 'object') return false
  const value = data as Record<string, unknown>
  return (
    value._bulk === true ||
    (value._invalidate === true && !value.id && !value.taskId)
  )
}

function matchesReader(
  key: QueryKey,
  data: unknown,
  organizationId: string,
  scope: TaskReaderScope,
) {
  if (!TASK_READER_ROOTS.has(String(key[0]))) return false
  const session = key.find(
    part => part && typeof part === 'object' && 'workspaceSession' in part,
  ) as { workspaceSession: unknown } | undefined
  if (
    session &&
    hashKey(session.workspaceSession as QueryKey) !== hashKey(scope.key)
  )
    return false
  if (key[0] === 'my-tasks')
    return (
      key[1] === 'v2' &&
      key[4] === organizationId &&
      (!scope.ownerUserId || key[2] === scope.ownerUserId)
    )
  const record =
    data && typeof data === 'object'
      ? (data as { organizationId?: string })
      : undefined
  return !record?.organizationId || record.organizationId === organizationId
}

const clients = new WeakMap<
  QueryClient,
  Map<string, ReturnType<typeof createEntry>>
>()

function createEntry(
  client: QueryClient,
  organizationId: string,
  scope: TaskReaderScope,
) {
  let subscribers = 0
  let timer: ReturnType<typeof setTimeout> | undefined
  let pending = false
  let readerDenied = false
  let running = false
  let disposed = false
  const seen = new Map<string, { at: number; denied: boolean }>()
  const current = () => {
    if (disposed || scope.signal.aborted) return false
    try {
      scope.assertUnchanged?.()
      return true
    } catch {
      return false
    }
  }
  function schedule() {
    if (!current() || running || timer !== undefined || !pending) return
    timer = setTimeout(() => {
      timer = undefined
      if (!current()) return
      const filters = {
        predicate: (query: { queryKey: QueryKey; state: { data: unknown } }) =>
          matchesReader(
            query.queryKey,
            query.state.data,
            organizationId,
            scope,
          ),
      }
      if (
        !readerDenied &&
        (scope.isEditing?.() ||
          tableEditingStore.getState().hasAnyOpenPopover())
      ) {
        void client.invalidateQueries({ ...filters, refetchType: 'none' })
        return
      }
      const denialRefresh = readerDenied
      pending = false
      readerDenied = false
      running = true
      const alreadyFetching = client
        .getQueryCache()
        .findAll(filters)
        .some(
          query => query.isActive() && query.state.fetchStatus === 'fetching',
        )
      void client
        .invalidateQueries(filters, { cancelRefetch: false })
        .finally(() => {
          running = false
          if (alreadyFetching) {
            pending = true
            readerDenied ||= denialRefresh
          }
          schedule()
        })
    }, 120)
  }
  const cancel = () => {
    clearTimeout(timer)
    timer = undefined
    pending = false
    readerDenied = false
    seen.clear()
  }
  const stopEditing = tableEditingStore.subscribe(() => {
    if (!tableEditingStore.getState().hasAnyOpenPopover()) schedule()
  })
  const stopTaskEditing = scope.subscribeEditing?.(schedule)
  scope.signal.addEventListener('abort', cancel, { once: true })
  return {
    retain() {
      subscribers++
    },
    release() {
      if (--subscribers > 0) return false
      disposed = true
      cancel()
      stopEditing()
      stopTaskEditing?.()
      scope.signal.removeEventListener('abort', cancel)
      return true
    },
    notify(eventId?: string, denied = false) {
      if (!current()) return
      const now = Date.now()
      for (const [key, value] of seen)
        if (now - value.at > 30_000) seen.delete(key)
      if (eventId) {
        const previous = seen.get(eventId)
        if (previous && (!denied || previous.denied)) return
        seen.set(eventId, { at: now, denied })
        if (seen.size > 512) seen.delete(seen.keys().next().value!)
      }
      readerDenied ||= denied
      pending = true
      schedule()
    },
  }
}

/** Share collection refreshes across rooms/listeners; preserve a change during an active fetch. */
export function subscribeTaskCollectionInvalidation(
  client: QueryClient,
  organizationId: string,
  scope: TaskReaderScope,
) {
  let entries = clients.get(client)
  if (!entries) {
    entries = new Map()
    clients.set(client, entries)
  }
  const key = hashKey([organizationId, scope.key])
  let entry = entries.get(key)
  if (!entry) {
    entry = createEntry(client, organizationId, scope)
    entries.set(key, entry)
  }
  const selected = entry
  selected.retain()
  let disposed = false
  return {
    notify(eventId?: string, denied = false) {
      if (!disposed) selected.notify(eventId, denied)
    },
    dispose() {
      if (disposed) return
      disposed = true
      if (selected.release()) entries.delete(key)
    },
  }
}
